import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { Church } from "lucide-react";

/**
 * NotificationPreview — Phase 14 (News Push Preview)
 *
 * A live, lock-screen-style preview of what the news push notification
 * will look like on iPhone. Mirrors the actual payload assembled by the
 * backend `_send_news_push`:
 *   • title = "Iglesia Nueva Cancion" (never varies)
 *   • body  = the news title in the selected language
 *
 * Includes a small ES/EN toggle button so an admin who is drafting an
 * item in one language can still see how it'll read in the other language
 * before pushing to members.
 *
 * Props
 *   titleEs, titleEn — bilingual news titles. Empty strings are fine; we
 *   fall back to a friendly placeholder so the preview never looks empty.
 *   defaultLang     — initial preview language ("es" or "en"). Defaults
 *   to the current UI language so a Spanish-mode admin sees Spanish first.
 */
export function NotificationPreview({ titleEs = "", titleEn = "", defaultLang }) {
  const { t, lang } = useI18n();
  const tNews = t.admin.newsForm;
  const [previewLang, setPreviewLang] = useState(defaultLang || lang || "es");

  const bodyText = (previewLang === "es"
    ? (titleEs || titleEn)
    : (titleEn || titleEs)
  ).trim();

  return (
    <div
      className="rounded-2xl border border-primary/20 bg-gradient-to-br from-slate-100 to-slate-50 p-3"
      data-testid="news-push-preview"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-muted-foreground">
          {tNews.previewTitle}
        </p>
        <div className="inline-flex rounded-full bg-white p-0.5 ring-1 ring-black/5">
          <Button
            type="button"
            size="sm"
            variant={previewLang === "es" ? "default" : "ghost"}
            className="h-7 rounded-full px-3 text-xs"
            onClick={() => setPreviewLang("es")}
            data-testid="news-push-preview-lang-es"
          >
            {tNews.previewLangEs}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={previewLang === "en" ? "default" : "ghost"}
            className="h-7 rounded-full px-3 text-xs"
            onClick={() => setPreviewLang("en")}
            data-testid="news-push-preview-lang-en"
          >
            {tNews.previewLangEn}
          </Button>
        </div>
      </div>

      {/* iOS-style lock screen notification card. Deliberately simple so
          it reads correctly even when the parent dialog is narrow. */}
      <div
        className="rounded-2xl bg-white/90 p-3 shadow-md ring-1 ring-black/5 backdrop-blur"
        data-testid="news-push-preview-card"
      >
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20">
            {/* No PWA icon URL here — we just use a symbolic church glyph
                so the preview stays self-contained. */}
            <Church className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <p
                className="truncate text-sm font-semibold text-slate-900"
                data-testid="news-push-preview-app-name"
              >
                {tNews.previewAppName}
              </p>
              <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-500">
                {tNews.previewNow}
              </span>
            </div>
            <p
              className="mt-0.5 line-clamp-2 text-sm text-slate-700"
              data-testid="news-push-preview-body"
            >
              {bodyText || tNews.previewEmptyBody}
            </p>
          </div>
        </div>
      </div>

      <p className="mt-2 text-[11px] text-muted-foreground">
        {tNews.previewSubtitle}
      </p>
    </div>
  );
}

export default NotificationPreview;
